import { css } from '@emotion/react'
import styled from '@emotion/styled'
import get from 'lodash/get'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import tw from 'tailwind.macro'

import LabelBreakPoints from '@components/common/LabelBreakPoints'

import { theme } from '@src/../tailwind.config'

const colors = theme.colors

interface Props {
  summaryView: React.ReactNode
  editView: (handler: () => void) => React.ReactNode
  titleCompact?: string
  title: string
  showPlaceholder: boolean
}

const Container = styled.div`
  ${tw`relative border rounded border-blue-300 bg-white`}
  z-index: 2;
`
const SummaryWrapper = styled.div`
  ${tw`px-2 flex select-none cursor-pointer`}
  min-height: 2.25rem;

  & > div {
    ${tw`flex items-center`}
  }
`
const SummaryContainer = styled.div`
  ${tw`flex justify-between flex-1`}
  height: 2.25rem;
`
const openedContainerCss = css({
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
})
const closedContainerCss = css({
  maxHeight: `max-content`,
})
const openedMainCss = css(tw`pointer-events-auto visible p-4 pb-3 border-t border-blue-300`, {
  maxHeight: `max-content`,
})
const closedMainCss = css(tw`absolute pointer-events-none invisible`, {
  maxHeight: 0,
})
const labelCSS = css(tw`text-2xs leading-none cursor-pointer flex items-center uppercase`, {
  fontSize: `13px`,
  fontWeight: theme.fontWeight.semibold,
  color: colors.blue[`500`],
  transform: `translateY(1px)`,
})

const DropDown = ({
  title,
  titleCompact,
  summaryView,
  editView,
  showPlaceholder,
  ...props
}: Props & Record<string, any>) => {
  const [opened, setOpened] = useState(false)
  const [mouseHandlerIsSet, changeMouseHandlerIsSet] = useState(false)
  const containerRef = useRef<HTMLDivElement>()

  const handledEditView: React.ReactNode = editView(() => setOpened(false))

  const handleMouseDown: EventListener = (evt: Event) => {
    if (containerRef.current && containerRef.current.contains(evt.target as HTMLElement)) {
      return
    }
    // Clicked outside:
    evt.preventDefault()
    evt.stopPropagation()
    setOpened(false)
  }
  useEffect(() => {
    const isInBrowser: boolean = typeof window !== `undefined`
    const cleanupFunction = () => {
      changeMouseHandlerIsSet(false)
      if (isInBrowser) {
        window.document.removeEventListener(`mousedown`, handleMouseDown)
      }
    }

    if (opened) {
      if (!mouseHandlerIsSet) {
        changeMouseHandlerIsSet(true)
        // A) Subscribe: Connect global document event listener:
        if (isInBrowser) {
          window.document.addEventListener(`mousedown`, handleMouseDown)
        }
      }
    } else {
      cleanupFunction()
    }

    // B) Unsubscribe: Return a cleanup function:
    return cleanupFunction
  }, [opened])

  return (
    <div className='relative' {...props}>
      <Container
        css={opened ? openedContainerCss : closedContainerCss}
        ref={containerRef as RefObject<HTMLDivElement>}
      >
        <SummaryWrapper
          onMouseDown={(evt) => {
            if (get(evt, `target.nodeName`) !== `DEL`) {
              evt.preventDefault()
              evt.stopPropagation()
              setOpened(!opened)
            }
          }}
        >
          {summaryView}
          <SummaryContainer>
            {showPlaceholder ? (
              <LabelBreakPoints compact={titleCompact} normal={title} serializedCSS={labelCSS} />
            ) : (
              <div />
            )}
            <div className='flex items-center h-8'>
              <svg
                width='14px'
                height='14px'
                viewBox='0 0 14 14'
                version='1.1'
                xmlns='http://www.w3.org/2000/svg'
                xmlnsXlink='http://www.w3.org/1999/xlink'
                style={{ width: `14px`, height: `14px` }}
                className='text-blue-500'
                role='img'
              >
                <g
                  id='add'
                  stroke='none'
                  strokeWidth='1'
                  fill='none'
                  fillRule='evenodd'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <g id='icn/add' transform='translate(1.000000, 1.000000)' stroke='currentColor'>
                    <path d='M6,0 L6,12' id='Shape'></path>
                    <path d='M12,6 L0,6' id='Shape'></path>
                  </g>
                </g>
              </svg>
            </div>
          </SummaryContainer>
        </SummaryWrapper>
        <div css={opened ? openedMainCss : closedMainCss}>{handledEditView}</div>
      </Container>
    </div>
  )
}

export default DropDown
